import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import type { ControlEvent } from '../../types/aai';
import { Action, Chip, Field, Input, Kicker, Modal, Select } from '../../components/aa';
import { relativeTime } from '../partner/format';
import { calendarFile, slotOptions } from './champion';
import {
  ACTION_KIND_LABEL, CONTROL_KINDS, kickerFor, payloadText, raisedBy, sentTo, shortName, sideOf, statusOf, stepsFor, subjectOf, teamOf,
} from './actionStatus';

/*
 * One drawer for every step in the response loop. It reads the action, offers
 * only the steps this person may take on it, records the step, and reads back
 * as completed. The other side is notified by the server.
 */

interface Readiness { maturity_pct: number | null; need_support: number | null; onboarded: number; suppressed: boolean }

const readinessOf = (value: unknown): Readiness | null => {
  if (!value || typeof value !== 'object') return null;
  return value as Readiness;
};

export const readinessLine = (r: Readiness): string => {
  if (r.suppressed || r.maturity_pct === null || r.maturity_pct === undefined) return `Protected · ${r.onboarded} onboarded`;
  let support = '';
  if (r.need_support !== null && r.need_support !== undefined) support = ` · ${r.need_support} need support`;
  return `${r.maturity_pct}% readiness${support}`;
};

const Tile: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-card border border-line bg-canvas-inset px-5 py-4">
    <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{label}</div>
    <div className="mt-1.5 break-words text-[15px] font-bold text-ink">{value}</div>
  </div>
);

export const ActionUpdateDrawer: React.FC<{ event: ControlEvent; onUpdated: () => void; onClose: () => void }> = ({ event, onUpdated, onClose }) => {
  const companyId = event.company_id;
  const me = localStorage.getItem('user_email') || '';
  const side = sideOf(event, me);
  const steps = stepsFor(event, me);
  const slots = slotOptions();
  const [status, setStatus] = useState(steps[0]?.status || '');
  const [note, setNote] = useState('');
  const [slotAt, setSlotAt] = useState(slots[0]?.at || '');
  const slot = slots.find((s) => s.at === slotAt)?.label || '';
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<ControlEvent | null>(null);

  const step = steps.find((s) => s.status === status) || null;
  const current = statusOf(event.status);
  const kind = ACTION_KIND_LABEL[event.kind] || 'Action';
  const isTeamKind = !CONTROL_KINDS.includes(event.kind) && event.kind !== 'capability_feedback' && event.kind !== 'customization_request';
  const recipients = sentTo(event);
  let sent = 'Nobody, record only';
  if (recipients.length > 0) sent = recipients.join(', ');
  let title = `${kind}: ${event.subject}`;
  let sub = 'Read only. This action was not sent to you and you did not raise it.';
  if (side === 'recipient') sub = `${shortName(raisedBy(event))} asked this of you. Answer on the record; they are notified.`;
  if (side === 'raiser') sub = 'You raised this. Close it on the record; the people it was sent to are notified.';
  if (steps.length === 0 && side !== 'reader') sub = 'This action is closed. Nothing more moves on it.';

  const submit = async () => {
    if (!step) return;
    let text = note.trim();
    if (step.slot) text = `Proposed slot: ${slot}. ${text}`.trim();
    if (step.needsNote && !note.trim()) {
      toast.error('Say why, in a sentence. The other side reads this.');
      return;
    }
    setBusy(true);
    try {
      const body: { status: string; note?: string; slot_at?: string; slot_label?: string } = { status: step.status, note: text || undefined };
      if (step.slot) {
        body.slot_at = slotAt;
        body.slot_label = slot;
      }
      const res = await aaiApi.respondControlEvent(event.id, body, companyId);
      setDone(res.data);
      toast.success(`${statusOf(step.status).label}. The other side has been notified.`);
      const nextStep = (res.data.payload || {}).next_step;
      if (typeof nextStep === 'string' && nextStep) toast.info(nextStep);
      onUpdated();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail || 'That did not record.');
    } finally {
      setBusy(false);
    }
  };

  let badge = <Chip tone={current.tone} dot>{current.label}</Chip>;
  if (done) badge = <Chip tone="ok">Completed</Chip>;
  let footer = (
    <div className="flex flex-wrap items-center justify-end gap-3">
      <Action size="lg" variant="quiet" onClick={onClose}>Cancel</Action>
      <Action size="lg" onClick={submit} disabled={busy || !step} reason="Nothing to record">{step?.label || 'Record'}</Action>
    </div>
  );
  if (done || steps.length === 0) footer = <div className="flex justify-end"><Action size="lg" variant="quiet" onClick={onClose}>Close</Action></div>;
  if (done) {
    title = `${kind}: ${event.subject}`;
    sub = `${statusOf(done.status).label} · recorded ${relativeTime(String((done.payload || {}).responded_at || ''))}`;
  }
  const history = Array.isArray((event.payload || {}).history) ? ((event.payload || {}).history as Array<{ status: string; by: string; at: string; note?: string }>) : [];
  const live = (done || event).payload || {};
  const booked = Boolean(live.session_id);
  const scheduledAt = typeof live.scheduled_at === 'string' ? live.scheduled_at : null;
  let bookedLine = `Session booked · ${String(live.invited || 0)} people invited · time to be confirmed`;
  if (scheduledAt) bookedLine = `Session booked · ${String(live.invited || 0)} people invited · ${new Date(scheduledAt).toLocaleString(undefined, { weekday: 'long', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`;
  const before = readinessOf(live.readiness_at_raise);
  const after = readinessOf(live.readiness_at_close);
  let afterLine = 'Read when the action is closed';
  if (after) afterLine = readinessLine(after);
  const downloadCalendar = () => {
    if (!scheduledAt) return;
    const file = new Blob([calendarFile(event.subject, scheduledAt, 60, payloadText(event, 'note') || '')], { type: 'text/calendar' });
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${event.subject.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Modal size="dialog" onClose={onClose} context={<Kicker>{kickerFor(event.kind)}</Kicker>} badge={badge} footer={footer}>
      <h2 className="m-0 text-[24px] font-bold leading-[31px] tracking-[-0.015em] text-ink">{title}</h2>
      <p className="m-0 mb-5 mt-2 text-[14px] leading-[23px] text-ink-muted">{sub}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {event.company_name && <Tile label="Company" value={event.company_name} />}
        {isTeamKind && <Tile label="Team" value={teamOf(event) || 'Whole company'} />}
        {!isTeamKind && <Tile label="About" value={subjectOf(event)} />}
        <Tile label="Raised by" value={`${raisedBy(event)} · ${relativeTime(event.created_at)}`} />
        <Tile label="Sent to" value={sent} />
        <Tile label="Detail" value={payloadText(event, 'note') || payloadText(event, 'response') || payloadText(event, 'scenario') || payloadText(event, 'slot') || 'No detail recorded'} />
      </div>

      {(booked || before) && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {booked && <Tile label="On the team's calendar" value={bookedLine} />}
          {booked && scheduledAt && (
            <div className="flex items-center rounded-card border border-line bg-canvas-inset px-5 py-4">
              <Action size="sm" variant="quiet" onClick={downloadCalendar}>Add to my calendar</Action>
            </div>
          )}
          {before && <Tile label="Readiness when raised" value={readinessLine(before)} />}
          {before && <Tile label="Readiness at close" value={afterLine} />}
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-6">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">So far</div>
          <div className="divide-y divide-line-soft rounded-card border border-line">
            {history.map((h, i) => (
              <div key={`${h.at}-${i}`} className="px-4 py-3">
                <div className="text-[13px] font-semibold text-ink">{statusOf(h.status).label} · {shortName(h.by)} · {relativeTime(h.at)}</div>
                {h.note && <div className="mt-0.5 text-[12.5px] text-ink-dim">{h.note}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {!done && steps.length > 0 && (
        <div className="mt-6 space-y-5">
          <Field label="Your step" htmlFor="action-step">
            <Select id="action-step" value={status} onChange={(e) => setStatus(e.target.value)}>
              {steps.map((s) => <option key={s.status} value={s.status}>{s.label}</option>)}
            </Select>
          </Field>
          {step?.slot && (
            <Field label="Proposed slot" htmlFor="action-slot">
              <Select id="action-slot" value={slotAt} onChange={(e) => setSlotAt(e.target.value)}>
                {slots.map((s) => <option key={s.at} value={s.at}>{s.label}</option>)}
              </Select>
            </Field>
          )}
          <Field label="Note for the other side" htmlFor="action-note">
            <Input id="action-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="One sentence they can act on" />
          </Field>
        </div>
      )}
    </Modal>
  );
};
